import { useState, useEffect } from 'react';

import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Users, AlertCircle, CheckCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import { workspaceMembersApi } from '@/lib/api/workspace-members.api';
import { useAuth } from '@/contexts/AuthContext';
import { initiateLoginRedirect } from '@/lib/oauth';
import type { InvitationDetailsResponse } from '@optidev/shared-types';

const roleBadgeColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-200',
  user: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200',
  viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-200',
};

/**
 * Invite Landing Page
 * URL: /invite/:token
 *
 * Shows invitation details and handles acceptance flow
 */
export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invitation, setInvitation] = useState<InvitationDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingMismatch, setConfirmingMismatch] = useState(false);

  // Fetch invitation details
  useEffect(() => {
    async function fetchInvitation() {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const data = await workspaceMembersApi.getInvitation(token);
        setInvitation(data);
      } catch (err: unknown) {
        const error = err as { response?: { status?: number } };
        if (error.response?.status === 404) {
          setError('This invitation is no longer valid');
        } else {
          setError('Failed to load invitation');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchInvitation();
  }, [token]);

  // Handle accept invitation. `confirmMismatchedEmail` is only ever passed true
  // after the user has explicitly clicked through the mismatch-confirmation UI.
  const handleAccept = async (confirmMismatchedEmail = false) => {
    if (!token) return;

    try {
      setAccepting(true);
      const result = await workspaceMembersApi.acceptInvitation(token, confirmMismatchedEmail);
      toast.success('Welcome to the workspace!');
      // Redirect to dashboard with new workspace
      navigate(`/dashboard?ws=${result.targetId}`);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; code?: string }, status?: number } };
      const message = error.response?.data?.message || '';

      // If invitation was already accepted (by auto-accept), treat as success
      if (message.toLowerCase().includes('accepted') || message.toLowerCase().includes('already a member')) {
        toast.success('Welcome to the workspace!');
        navigate(`/dashboard?ws=${invitation?.workspace?.id}`);
      } else if (error.response?.data?.code === 'EMAIL_MISMATCH') {
        // Auto-accept attempted this without the user asking — just let them
        // see the mismatch screen and decide, don't toast an error.
      } else {
        toast.error(message || 'Failed to accept invitation');
      }
    } finally {
      setAccepting(false);
    }
  };

  // Handle sign in - redirect to OAuth with return URL
  const handleSignIn = () => {
    // Store the current URL so user returns here after login
    const returnUrl = window.location.href;
    initiateLoginRedirect(returnUrl);
  };

  // Auto-accept if user is logged in with matching email
  useEffect(() => {
    if (
      user &&
      invitation?.status === 'pending' &&
      user.email?.toLowerCase() === invitation.email.toLowerCase()
    ) {
      // Auto-accept
      handleAccept();
    }
  }, [user, invitation]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Invitation Not Found</CardTitle>
            <CardDescription>
              {error || 'This invitation link is no longer valid.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/')}>Go to Homepage</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already accepted - show welcome message
  if (invitation.status === 'accepted') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <CardTitle>Welcome to {invitation.workspace?.name || 'the workspace'}!</CardTitle>
            <CardDescription>
              You've joined as {invitation.role === 'admin' ? 'an' : 'a'}{' '}
              <span className="font-medium capitalize">{invitation.role}</span>.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate(`/dashboard?ws=${invitation.workspace?.id}`)}>
              Go to Workspace
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired or revoked
  if (invitation.status === 'expired' || invitation.status === 'revoked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <CardTitle>Invitation {invitation.status === 'expired' ? 'Expired' : 'Revoked'}</CardTitle>
            <CardDescription>
              {invitation.status === 'expired'
                ? 'This invitation has expired. Please ask for a new one.'
                : 'This invitation has been cancelled.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending invitation - show details
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-2xl mx-auto">
              {invitation.workspace?.name?.charAt(0).toUpperCase() || 'W'}
            </div>
          </div>
          <CardTitle>You're invited to join</CardTitle>
          <div className="text-2xl font-bold mt-2">
            {invitation.workspace?.name || 'a workspace'}
          </div>
          <CardDescription className="mt-4">
            <span className="flex items-center justify-center gap-2">
              Role:
              <Badge className={roleBadgeColors[invitation.role]}>
                {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
              </Badge>
            </span>
            <span className="block mt-2">
              Invited by {invitation.invitedBy.displayName || invitation.invitedBy.email}
            </span>
            {invitation.workspace?.memberCount && (
              <span className="flex items-center justify-center gap-1 mt-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                {invitation.workspace.memberCount} member
                {invitation.workspace.memberCount !== 1 ? 's' : ''}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {user ? (
            // User is logged in
            user.email?.toLowerCase() === invitation.email.toLowerCase() ? (
              <Button
                className="w-full"
                size="lg"
                onClick={() => handleAccept()}
                disabled={accepting}
              >
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Joining...
                  </>
                ) : (
                  'Accept Invitation'
                )}
              </Button>
            ) : confirmingMismatch ? (
              <div className="text-center text-sm space-y-3">
                <p className="text-muted-foreground">
                  You'll join {invitation.workspace?.name || 'the workspace'} as{' '}
                  <strong>{user.email}</strong>, not {invitation.email}. Only continue
                  if {invitation.email} is really you (e.g. an alias of this account).
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setConfirmingMismatch(false)}
                    disabled={accepting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => handleAccept(true)}
                    disabled={accepting}
                  >
                    {accepting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Joining...
                      </>
                    ) : (
                      `Yes, join as ${user.email}`
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center text-sm space-y-3">
                <p className="text-destructive">
                  This invitation was sent to <strong>{invitation.email}</strong>, but
                  you're signed in as <strong>{user.email}</strong>.
                </p>
                <p className="text-muted-foreground">
                  If {invitation.email} is an alias of your account, you can accept it
                  as {user.email} below. Otherwise, sign out and sign in with the{' '}
                  {invitation.email} account, or ask{' '}
                  {invitation.invitedBy.displayName || invitation.invitedBy.email} to
                  resend the invite to <strong>{user.email}</strong> instead.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setConfirmingMismatch(true)}
                >
                  Accept as {user.email} instead
                </Button>
              </div>
            )
          ) : (
            // User not logged in
            <>
              <Button className="w-full" size="lg" onClick={handleSignIn}>
                Continue with OptiPrime
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Sign in or create an account to join this workspace
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
